import React, { useEffect, useState, useCallback } from 'react';
import type { User } from 'firebase/auth';
import { auth } from "@/config/firebase";
import { useNavigate, useLocation } from 'react-router-dom';
import { loginWithGoogle } from '@/services/auth/firebase-auth';
import AuthContext from './AuthContext';

const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      setCurrentUser(user);
      setLoading(false);
      
      // Redirect after login when user changes from null to defined
      if (user && location.pathname === '/login') {
      navigate('/', { replace: true });
      }
    });
    
    return unsubscribe;
  }, [navigate, location.pathname]);

  // Google sign-in handler
  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const success = await loginWithGoogle();
      if (!success) {
        setError("Google login failed. Please try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Logout function
  const logout = useCallback(async () => {
    try {
      await auth.signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to log out");
    }
  }, [navigate]);

  const value = {
    currentUser,
    loading,
    error,
    signInWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
